import React, { useCallback, useContext, useState } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import clsx from 'clsx';
import { BiExport } from 'react-icons/bi';
import { FiFileText } from 'react-icons/fi';

import {
  ButtonTitleWithLoading,
  ConfirmDialog,
  CustomizeDashboard,
  EmptyList,
} from '@vyce/core/src/components';
import { WidgetItem, TEAMS_PERMISSIONS } from '@vyce/core/src/types';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { AppDashboard } from '@vyce/core/src/components/Dashboard/AppDashboard';
import { TeamsDashboardModuleContext } from '@vyce/core/src/contexts';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { HealthSafetyWidget } from '@vyce/core/src/views/widgets/HealthSafetyWidget';
import { QualificationWidget } from '@vyce/core/src/views/widgets/QualificationWidget';
import config from '@vyce/core/src/assets/config';
import { RecentlyRegisteredWidget } from '../../views/widgets/RecentlyRegisteredWidget';
import { ProximityToWorkWidget } from '../../views/widgets/ProximityToWorkWidget';
import { RequestReportDialog } from '../../views/teamsReports';

import { useTeamsDashboardData } from './hooks';
import { getRecentlyRegisteredRequest } from '../../api/dashboards';

export const TeamsDashboard = () => {
  const horizontalScrollClasses = useHorizontalScrollStyles();

  const {
    mainDashboardFilters,
    teamPermission,
    updateMainDashboardFilters,
    widgetComponent,
    onOpenUniversalInviteForm,
    inviteButtonComponent,
    chatUser,
    chatCompanyId,
    chatRoute,
    reports,
  } = useContext(TeamsDashboardModuleContext);
  const { canAnywhere } = usePermissions();
  const canManageQualifications = canAnywhere(TEAMS_PERMISSIONS.CAN_CREATE_QUALIFICATION);
  const canRequestReports = !!reports && canAnywhere(TEAMS_PERMISSIONS.CAN_GENERATE_REPORTS);
  const [isReportDialogOpen, setReportDialogOpen] = useState(false);

  const {
    isMobile,
    first_name,
    companyDataLoading,
    teamsDashboard,
    teams,
    selectedCompanyId,
    selectedTeamIds,
    buttonLoading,
    isNotifyDialogOpen,
    onWidgetsChangeViaSettings,
    resetDashboards,
    closeNotifyDialog,
    exportQualifications,
    onWidgetStatusChange,
    onLayoutChange,
    goToTeamsList,
  } = useTeamsDashboardData({});

  const getWidget = useCallback(
    (widget: WidgetItem) => {
      switch (widget.system_name) {
        case 'team_workers':
          return undefined;
        case 'team_health_and_safety':
          return {
            component: HealthSafetyWidget,
            props: { companyId: selectedCompanyId, teamIds: selectedTeamIds },
          };
        case 'teams_qualifications':
          return {
            component: QualificationWidget,
            props: { selectedCompanyId, mainDashboardFilters, chatUser, chatCompanyId, chatRoute },
          };
        case 'recent_registered_employees':
          return {
            component: RecentlyRegisteredWidget,
            props: {
              selectedCompanyId,
              mainDashboardFilters,
              moduleType: 'teams',
              fetchRecentlyRegisteredWorkers: getRecentlyRegisteredRequest,
              chatUser,
            },
          };
        case 'proximity_to_work':
          return {
            component: ProximityToWorkWidget,
            props: {
              selectedCompanyId,
              mainDashboardFilters,
            },
          };

        default:
          return {
            component: widgetComponent,
            props: { widget, redirectUrl: '/teams/members', mainDashboardFilters },
          };
      }
    },
    [selectedCompanyId, selectedTeamIds, mainDashboardFilters]
  );

  return (
    <>
      <Box
        className={clsx(
          horizontalScrollClasses.blockWrapper,
          horizontalScrollClasses.blockWithHideScroll,
          horizontalScrollClasses.navBarBlock
        )}>
        {!isMobile && (
          <Box display="flex">
            <Typography
              variant="subtitle1"
              style={{ marginRight: 4, fontWeight: 600, whiteSpace: 'nowrap' }}
              color="secondary">
              Hey {first_name},
            </Typography>
            <Typography variant="subtitle1" style={{ whiteSpace: 'nowrap' }} color="textSecondary">
              here’s your Teams dashboard...
            </Typography>
          </Box>
        )}
        <Box display="flex" gridGap={16}>
          {inviteButtonComponent ? (
            React.createElement(inviteButtonComponent)
          ) : (
            <Button
              onClick={onOpenUniversalInviteForm}
              size="small"
              data-tour="invite"
              startIcon={<AiOutlinePlus />}
              variant="contained"
              color="primary">
              Invite New Members
            </Button>
          )}

          {canRequestReports && (
            <Button
              onClick={() => setReportDialogOpen(true)}
              startIcon={<FiFileText size="15px" />}
              variant="contained"
              size="small"
              color="primary">
              Request report
            </Button>
          )}

          {canManageQualifications && (
            <Button
              onClick={exportQualifications}
              startIcon={!buttonLoading && <BiExport size="15px" />}
              style={{ width: 234 }}
              variant="contained"
              size="small"
              color="secondary">
              <ButtonTitleWithLoading
                title="Request Qualifications Matrix"
                loaderVariant="paper"
                loading={buttonLoading}
              />
            </Button>
          )}

          <CustomizeDashboard
            widgetItems={
              (teamsDashboard?.widgets?.filter(widget => widget.system_name !== 'team_workers') ||
                []) as WidgetItem[]
            }
            companyId={selectedCompanyId}
            mainDashboardFiltersFromState={mainDashboardFilters}
            teamPermission={teamPermission}
            timePermission={false}
            updateMainDashboardFilters={updateMainDashboardFilters}
            onWidgetsChangeViaSettings={onWidgetsChangeViaSettings}
            resetDashboards={resetDashboards}
          />
        </Box>
      </Box>

      {!teams.length && !companyDataLoading ? (
        <EmptyList
          image={config.icons3D.lightning}
          title="We haven't found any team members."
          subtitle="Please add or invite team members to see data about you teams."
          buttonText="Manage My Teams"
          callback={goToTeamsList}
        />
      ) : (
        <Box style={{ marginRight: '-24px', marginLeft: '-24px' }}>
          <AppDashboard
            layouts={teamsDashboard?.layouts}
            onLayoutChange={onLayoutChange}
            widgets={teamsDashboard?.widgets || []}
            getWidget={getWidget}
            onWidgetStatusChange={onWidgetStatusChange}
          />
        </Box>
      )}
      {canRequestReports && reports && (
        <RequestReportDialog
          open={isReportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          companyId={selectedCompanyId}
          teams={teams}
          {...reports}
        />
      )}
      <ConfirmDialog
        handleClose={closeNotifyDialog}
        open={isNotifyDialogOpen}
        confirmText="OK"
        showCancelText={false}
        title="We’re building your Qualifications Matrix now and will email it to you as soon as it’s ready!"
        handleConfirm={closeNotifyDialog}
      />
    </>
  );
};
